import { API } from "@/lib/api";

const ANON_KEY = "bbc.analytics.anonymous";
const SESSION_KEY = "bbc.analytics.session";
let queue = [];
let timer = null;

function uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function stored(storage, key) {
  try {
    let value = storage.getItem(key);
    if (!value) {
      value = uuid();
      storage.setItem(key, value);
    }
    return value;
  } catch {
    return uuid();
  }
}

const anonymousId = stored(window.localStorage, ANON_KEY);
const sessionId = stored(window.sessionStorage, SESSION_KEY);

function safeName(value) {
  return String(value || "").trim().replace(/[^a-zA-Z0-9._:/-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 128);
}

function safePath(path) {
  return String(path || "/").split("?")[0].split("#")[0].slice(0, 512);
}

async function flush(useBeacon = false) {
  if (!queue.length) return;
  const events = queue.splice(0, 50);
  const payload = JSON.stringify({ events });
  if (useBeacon && navigator.sendBeacon) {
    const sent = navigator.sendBeacon(`${API}/analytics/events`, new Blob([payload], { type: "application/json" }));
    if (sent) return;
  }
  try {
    await fetch(`${API}/analytics/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      keepalive: useBeacon,
      body: payload,
    });
  } catch {
    // Analytics must never interrupt the user experience.
  }
}

function enqueue(event, immediate = false) {
  queue.push({
    session_id: sessionId,
    anonymous_id: anonymousId,
    platform: "website",
    ...event,
    name: safeName(event.name),
    path: safePath(event.path),
  });
  if (queue.length >= 10 || immediate) {
    clearTimeout(timer);
    timer = null;
    void flush(immediate);
  } else if (!timer) {
    timer = setTimeout(() => { timer = null; void flush(); }, 2500);
  }
}

export function trackPageView(path) {
  enqueue({ event_type: "page_view", name: safePath(path) || "/", path });
}

export function trackSection(name, path) {
  enqueue({ event_type: "section_view", name, path, context: { section: name } });
}

export function trackSectionTime(name, path, durationMs, immediate = false) {
  const duration = Math.max(0, Math.min(Math.round(durationMs), 86400000));
  if (duration < 1000) return;
  enqueue({ event_type: "section_time", name, path, duration_ms: duration, context: { section: name } }, immediate);
}

export function trackFeature(name, path, context = {}) {
  enqueue({ event_type: "feature_click", name, path, context });
}

export function trackSessionEnd(name, path, durationMs, scrollDepth) {
  enqueue({
    event_type: "session_end", name: name || safePath(path) || "/", path,
    duration_ms: Math.max(0, Math.min(Math.round(durationMs), 86400000)),
    context: { scroll_depth: Math.max(0, Math.min(Math.round(scrollDepth), 100)) },
  }, true);
}
